import React from 'react';
import {
  MdNotificationsNone,
  MdSend, MdSwapHoriz, MdCameraAlt, MdReceipt, MdSearch,
  MdPhoneInTalk, MdCallEnd, MdCheckCircleOutline, MdChevronRight,
} from 'react-icons/md';
import { useProfile } from '../contexts/ProfileContext';
import { useApp } from '../contexts/AppContext';
import { maskAmount } from '../contexts/AppContext';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { fetchTodayFeed, previewAction, executeAction, setAutoCoverShortfalls } from '../services/backendAPI';

const CallStatusBanner = ({ status, onHangUp }) => {
  const [dismissWarning, setDismissWarning] = React.useState(false);

  React.useEffect(() => {
    setDismissWarning(false);
  }, [status]);

  if (status === 'active') {
    return (
      <div className="call-status-banner active-call-bg" style={{flexDirection:'column', alignItems:'stretch', gap:0}}>
        <div style={{display:'flex', alignItems:'center'}}>
          <div className="csb-left" style={{flex:1}}>
            <div className="csb-avatar-pulse">
              <MdPhoneInTalk size={20} color="white" />
            </div>
            <div className="csb-info">
              <div className="csb-title">On a call with you</div>
              <div className="csb-subtitle">0:45 • Hawa from TD</div>
            </div>
          </div>
        </div>

        {!dismissWarning && (
          <div
            className="csb-warning"
            style={{
              margin: '8px 0 4px',
              fontSize: 13,
              color: '#000',
              background: 'rgba(255,255,255,0.95)',
              padding: '8px 10px',
              borderRadius: 6,
            }}
          >
            <div style={{marginBottom: 6}}>
              Never share your one-time TD code — we will <strong>never</strong> ask for it.
            </div>
            <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
              <button
                onClick={() => setDismissWarning(true)}
                aria-label="Dismiss warning"
                style={{
                  background: '#ffffff',
                  border: '1px solid #d0d0d0',
                  color: '#555',
                  padding: '5px 12px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                Dismiss
              </button>
              <button
                className="emergency-hangup-btn"
                onClick={(e) => { e.stopPropagation(); onHangUp('none'); }}
                style={{margin:0}}
              >
                <MdCallEnd size={16} />
                <span>Hang Up Now</span>
              </button>
            </div>
          </div>
        )}

        {dismissWarning && (
          <div style={{display:'flex', justifyContent:'flex-end', marginTop:6}}>
            <button
              className="emergency-hangup-btn"
              onClick={(e) => { e.stopPropagation(); onHangUp('none'); }}
              style={{margin:0}}
            >
              <MdCallEnd size={16} />
              <span>Hang Up</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  if (status === 'recent') {
    return (
      <div className="call-status-banner recent-call-bg">
        <div className="csb-left">
          <MdCheckCircleOutline size={22} color="var(--td-green)" />
          <div className="csb-info">
            <div className="csb-title dark-text">No active call from TD</div>
            <div className="csb-subtitle gray-text">If someone says they're from TD, hang up.</div>
          </div>
        </div>
        <MdChevronRight className="csb-arrow gray-text" />
      </div>
    );
  }

  return (
    <div className="call-status-banner recent-call-bg">
      <div className="csb-left">
        <MdPhoneInTalk size={22} color="var(--text-medium)" />
        <div className="csb-info">
          <div className="csb-title dark-text">Last called: Today, 10:15 AM</div>
          <div className="csb-subtitle gray-text">Subject: Security Verification</div>
        </div>
      </div>
      <MdChevronRight className="csb-arrow gray-text" />
    </div>
  );
};

// "Today" card — the top-of-hub surface the whole Day in the Life (200–280)
// series attaches to. Lists up to 3 feed situations; items with an action expose
// a confirm-gated button reusing the existing preview → confirm → execute UX.
const SEVERITY_DOT = { high: 'var(--td-red)', medium: '#d68910', low: 'var(--td-green)' };

const TodayCard = ({ sessionId }) => {
  const { simDate } = useApp();
  const [feed, setFeed] = React.useState([]);
  const [autoCover, setAutoCover] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  // Offers the customer has redeemed this session, so the chip flips to "redeemed"
  // without a full profile reload.
  const [redeemed, setRedeemed] = React.useState({});

  const load = React.useCallback(async () => {
    if (!sessionId) return;
    const d = await fetchTodayFeed(sessionId, simDate || null);
    if (d?.ready && Array.isArray(d.feed)) setFeed(d.feed);
    if (d && typeof d.autoCoverShortfalls === 'boolean') setAutoCover(d.autoCoverShortfalls);
  }, [sessionId, simDate]);

  React.useEffect(() => { load(); }, [load]);

  // Persist the opt-in "do this automatically next time" preference, then reload
  // so the shortfall item reflects the new autoResolvable framing.
  const toggleAutoCover = React.useCallback(async (enabled) => {
    if (!sessionId || busy) return;
    setAutoCover(enabled); // optimistic
    const res = await setAutoCoverShortfalls(sessionId, enabled);
    if (res?.ok) await load();
    else setAutoCover(!enabled); // revert on failure
  }, [sessionId, busy, load]);

  // Confirm-gated action: preview → confirm the amount → execute → refresh feed.
  // An EMT REQUEST (split the bill) also needs a recipient, so we collect that
  // first; the amount then defaults to the pre-filled per-person share.
  const runAction = React.useCallback(async (action) => {
    if (!sessionId || !action?.type || busy) return;
    setBusy(true);
    try {
      // Smarter-choice reward (plan 250): a non-financial log — no amount/recipient
      // prompt, just a confirm, then record the swap so points + the tally grow.
      if (action.type === 'log_smart_choice') {
        const pts = Number(action.params?.points) || 0;
        const saved = Number(action.params?.estSavings) || 0;
        if (!window.confirm(`Log this swap — ${action.params?.from} → ${action.params?.to}?\n\nEstimated ~$${saved.toLocaleString()} saved${pts ? ` · +${pts} pts` : ''}. (Estimated figures.)`)) return;
        const res = await executeAction(sessionId, action.type, action.params || {});
        if (res?.ok) await load();
        else window.alert(res?.reason || 'That could not be completed.');
        return;
      }

      // Daily check-in (plan 280): a NON-financial stamp — record today's scorecard
      // result so the streak advances. Just a confirm, then refresh the feed.
      if (action.type === 'daily_checkin') {
        const met = Number(action.params?.metCount);
        const tot = Number(action.params?.totalCount);
        const score = Number.isFinite(met) && Number.isFinite(tot) ? ` — ${met} of ${tot} on track` : '';
        if (!window.confirm(`Check in for today${score}?\n\nThis records today's progress and keeps your streak going. No money moves.`)) return;
        const res = await executeAction(sessionId, action.type, action.params || {});
        if (res?.ok) await load();
        else window.alert(res?.reason || 'That could not be completed.');
        return;
      }

      // Fraud remediation (plan 270): a NON-financial bundle — freeze the card,
      // open a dispute, notify — behind a single confirm. No amount/recipient
      // prompt; one tap does everything (and the freeze is reversible).
      if (action.type === 'handle_fraud') {
        const m = action.params?.merchant || 'this charge';
        const amt = Number(action.params?.amount) || 0;
        if (!window.confirm(`Handle this for you?\n\nI'll freeze your card, open a dispute on ${m}${amt ? ` ($${amt.toLocaleString()})` : ''}, and notify you.\n\nNo money moves — the charge is marked under dispute, and you can unfreeze anytime.`)) return;
        const res = await executeAction(sessionId, action.type, action.params || {});
        if (res?.ok) { window.alert('Done — card frozen and a dispute opened. You can unfreeze it in Card Management.'); await load(); }
        else window.alert(res?.reason || 'That could not be completed.');
        return;
      }

      const isRequest = action.type === 'request_email_money_transfer';
      let recipient = action.params?.recipient;
      if (isRequest) {
        recipient = window.prompt('Who was this shared with? Enter a name to request their share from:', recipient || '');
        if (recipient == null || !recipient.trim()) return;
      }
      const previewParams = { ...(action.params || {}), ...(recipient ? { recipient } : {}) };
      const pv = await previewAction(sessionId, action.type, previewParams);
      if (!pv?.ok) { window.alert(pv?.reason || "Can't do that right now."); return; }
      const promptMsg = isRequest
        ? `${pv.summary}.\n\nEnter the amount to request:`
        : `${pv.summary}.\n\nEnter an amount (max $${pv.amount.toLocaleString()}):`;
      const raw = window.prompt(promptMsg, String(pv.amount));
      if (raw == null) return;
      const amount = Number(raw);
      if (!(amount > 0)) return;
      const res = await executeAction(sessionId, action.type, { ...previewParams, amount });
      if (res?.ok) await load();
      else window.alert(res?.reason || 'That could not be completed.');
    } finally {
      setBusy(false);
    }
  }, [sessionId, busy, load]);

  // Redeem a partner offer — a NON-financial, confirm-gated action (records intent;
  // no money moves). On success we flip the chip to "redeemed" locally.
  const redeemOffer = React.useCallback(async (offer) => {
    if (!sessionId || !offer?.id || busy || redeemed[offer.id]) return;
    setBusy(true);
    try {
      const pv = await previewAction(sessionId, 'redeem_offer', {
        offerId: offer.id, estimatedSavings: offer.estimatedSavings,
      });
      if (!pv?.ok) { window.alert(pv?.reason || "That offer can't be redeemed right now."); return; }
      if (!window.confirm(`${pv.summary}?\n\n${offer.terms || ''}${offer.code ? `\n\nCode: ${offer.code}` : ''}`)) return;
      const res = await executeAction(sessionId, 'redeem_offer', {
        offerId: offer.id, estimatedSavings: offer.estimatedSavings,
      });
      if (res?.ok) setRedeemed((r) => ({ ...r, [offer.id]: true }));
      else window.alert(res?.reason || 'That could not be completed.');
    } finally {
      setBusy(false);
    }
  }, [sessionId, busy, redeemed]);

  if (!sessionId || !feed.length) return null;

  // Show the top 3 situations, but always keep the daily check-in summary (plan
  // 280) visible — it's low-severity so it can fall outside the top 3, yet it's
  // the one item that tracks the day's overall progress + streak.
  const checkinItem = feed.find((e) => e.kind === 'daily_checkin');
  const topItems = feed.slice(0, 3);
  const displayFeed = checkinItem && !topItems.includes(checkinItem)
    ? [...topItems, checkinItem]
    : topItems;

  // The card surface (.bh-account-card = --bh-white) stays white in BOTH themes,
  // so text must use fixed dark inks — the themed --text-* vars flip to light
  // cream/lavender in the purple (UGO) theme and become unreadable on white.
  const INK = '#1a1a1a';        // primary text
  const INK_MUTED = '#555555';  // secondary / captions

  return (
    <section className="bh-section" aria-label="Today">
      <h2 className="bh-section-title">Today</h2>
      <div className="bh-account-list">
        {displayFeed.map((e) => (
          <div key={e.id} className="bh-account-card" style={{ cursor: 'default', textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: SEVERITY_DOT[e.severity] || 'var(--td-green)', display: 'inline-block',
              }} />
              <p className="bh-account-card-type" style={{ margin: 0, font: 'var(--bh-body)', fontWeight: 700, color: INK }}>{e.title}</p>
              {e.autoResolvable && (
                <span style={{
                  marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: 'var(--td-green)',
                  border: '1px solid var(--td-green)', borderRadius: 10, padding: '1px 7px',
                }}>Auto-covered</span>
              )}
            </div>
            <p style={{ fontSize: 13, color: INK, margin: '4px 0 0 16px' }}>{e.detail}</p>

            {/* Shortfall: dated debit timeline showing which one breaches. */}
            {e.kind === 'upcoming_shortfall' && Array.isArray(e.timeline) && e.timeline.length > 0 && (
              <ul style={{ listStyle: 'none', margin: '8px 0 0 16px', padding: 0 }}>
                {e.timeline.map((d, i) => (
                  <li key={`${d.name}-${d.dueDate}-${i}`} style={{
                    display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 13,
                    color: d.willClear ? INK_MUTED : 'var(--td-red)', padding: '2px 0',
                  }}>
                    <span style={{ flexShrink: 0 }}>{d.willClear ? '✓' : '⚠'}</span>
                    <span style={{ fontWeight: d.willClear ? 400 : 600 }}>{d.name}</span>
                    <span style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                      ${Number(d.amount).toLocaleString()} · {d.dueDate}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* Fraud alert (plan 270): the explainable signals that fired, so a
                false positive is easy to understand and dismiss. */}
            {e.kind === 'fraud_alert' && Array.isArray(e.signals) && e.signals.length > 0 && (
              <ul style={{ listStyle: 'none', margin: '8px 0 0 16px', padding: 0 }}>
                {e.signals.map((s, i) => (
                  <li key={`${s.code}-${i}`} style={{
                    display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 13,
                    color: 'var(--td-red)', padding: '2px 0',
                  }}>
                    <span style={{ flexShrink: 0 }}>⚠</span>
                    <span>{s.label}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Budget guardrail: spent/limit progress bar, colored by severity. */}
            {e.kind === 'budget' && typeof e.limit === 'number' && (
              <div style={{ margin: '8px 0 0 16px' }}>
                <div style={{
                  position: 'relative', height: 8, borderRadius: 4,
                  background: 'var(--border-light, #e0e0e0)', overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${Math.min(100, e.pct || 0)}%`, height: '100%', borderRadius: 4,
                    background: SEVERITY_DOT[e.severity] || 'var(--td-green)',
                  }} />
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: 12, color: INK_MUTED, marginTop: 3,
                }}>
                  <span>${Number(e.amount || 0).toLocaleString()} of ${Number(e.limit).toLocaleString()}</span>
                  <span>{e.pct}%{typeof e.projected === 'number' && e.projected > e.limit
                    ? ` · projected $${Math.round(e.projected).toLocaleString()}` : ''}</span>
                </div>
              </div>
            )}

            {/* Daily check-in (plan 280): the scorecard items (met/missed/na), the
                streak, and any gap-driven service recommendation. */}
            {e.kind === 'daily_checkin' && e.scorecard && (
              <div style={{ margin: '8px 0 0 16px' }}>
                {e.streak && (e.streak.currentStreak > 0 || e.streak.bestStreak > 0) && (
                  <p style={{ fontSize: 13, color: INK_MUTED, margin: '0 0 6px' }}>
                    🔥 {e.streak.currentStreak}-day streak
                    {e.streak.bestStreak > e.streak.currentStreak ? ` · best ${e.streak.bestStreak}` : ''}
                  </p>
                )}
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {(e.scorecard.items || []).filter((it) => it.status !== 'na').map((it) => (
                    <li key={it.id} style={{
                      display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 13,
                      color: it.status === 'met' ? INK_MUTED : 'var(--td-red)', padding: '2px 0',
                    }}>
                      <span style={{ flexShrink: 0 }}>{it.status === 'met' ? '✓' : '⚠'}</span>
                      <span style={{ fontWeight: it.status === 'met' ? 400 : 600 }}>{it.label}</span>
                    </li>
                  ))}
                </ul>
                {Array.isArray(e.services) && e.services.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {e.services.map((s) => (
                      <div key={s.id} style={{
                        fontSize: 12, color: INK_MUTED, padding: '4px 0',
                        borderTop: '1px solid var(--border-light, #e0e0e0)',
                      }}>
                        <strong style={{ color: INK }}>{s.name}</strong> — {s.why}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {e.action && (
              <button
                className="home-promo-btn"
                style={{ marginTop: 10, marginLeft: 16 }}
                disabled={busy}
                onClick={() => runAction(e.action)}
              >
                {e.action.type === 'cover_shortfall'
                  ? `Cover $${Number(e.action.params?.amount || e.amount || 0).toLocaleString()} from savings`
                  : e.action.type === 'request_email_money_transfer'
                    ? `Request $${Number(e.action.params?.amount || e.perPerson || 0).toLocaleString()} from someone`
                    : e.action.type === 'log_smart_choice'
                      ? `Log this swap${e.points ? ` (+${e.points} pts)` : ''}`
                      : e.action.type === 'handle_fraud'
                        ? 'Handle it for me'
                        : e.action.type === 'daily_checkin'
                          ? 'Check in'
                          : 'Take action'}
              </button>
            )}

            {/* Partner offers (plan 230): a chip per matched offer showing the
                estimated savings; tap to see terms + code and record redemption. */}
            {Array.isArray(e.offers) && e.offers.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '10px 0 0 16px' }}>
                {e.offers.map((o) => (
                  <button
                    key={o.id}
                    className="home-promo-btn"
                    disabled={busy || redeemed[o.id]}
                    onClick={() => redeemOffer(o)}
                    title={o.terms || ''}
                    style={{
                      margin: 0, fontSize: 12,
                      opacity: redeemed[o.id] ? 0.6 : 1,
                      background: redeemed[o.id] ? 'var(--td-green)' : undefined,
                    }}
                  >
                    {redeemed[o.id]
                      ? `✓ Redeemed — ${o.partner}`
                      : `Save ~$${Math.round(Number(o.estimatedSavings) || 0).toLocaleString()} with ${o.partner} ${o.title}`}
                  </button>
                ))}
              </div>
            )}

            {/* Opt-in: remember this choice so future shortfalls are pre-covered. */}
            {e.kind === 'upcoming_shortfall' && (
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8,
                margin: '10px 0 0 16px', fontSize: 13, color: INK_MUTED, cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={autoCover}
                  disabled={busy}
                  onChange={(ev) => toggleAutoCover(ev.target.checked)}
                />
                Do this automatically next time
              </label>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

// Resolve the display balance from an account object — field names vary by type.
function resolveBalance(acct) {
  if (!acct || typeof acct !== 'object') return null;
  const v = acct.current_balance ?? acct.current_balance_owing ??
            acct.current_value ?? acct.current_market_value ?? acct.balance ?? null;
  return v != null ? Number(v) : null;
}

// Per-key metadata: label fallback, group, isCredit, optional fixed route.
const ACCT_META = {
  chequing:          { label: 'Chequing',                group: 'deposit',  isCredit: false },
  personal_chequing: { label: 'Personal Chequing',       group: 'deposit',  isCredit: false },
  savings:           { label: 'Savings',                 group: 'deposit',  isCredit: false },
  personal_savings:  { label: 'Savings',                 group: 'deposit',  isCredit: false },
  business_chequing: { label: 'Business Chequing',       group: 'deposit',  isCredit: false },
  tfsa:              { label: 'TFSA',                    group: 'invest',   isCredit: false },
  rrsp:              { label: 'RRSP',                    group: 'invest',   isCredit: false },
  fhsa:              { label: 'FHSA',                    group: 'invest',   isCredit: false },
  resp:              { label: 'RESP',                    group: 'invest',   isCredit: false },
  investments:       { label: 'Investments',             group: 'invest',   isCredit: false },
  investment_account:{ label: 'Investment Account',      group: 'invest',   isCredit: false },
  credit_card:       { label: 'Credit Card',             group: 'credit',   isCredit: true  },
  business_loc:      { label: 'Business Line of Credit', group: 'credit',   isCredit: true  },
  mortgage:          { label: 'Mortgage',                group: 'credit',   isCredit: true, route: 'mortgageDetail' },
};
const GROUP_ORDER = ['deposit', 'invest', 'credit'];
const GROUP_LABEL = { deposit: 'Accounts', invest: 'Registered & Investing', credit: 'Credit & Loans' };

const HomeScreen = ({ onNavigate, onSetCallState }) => {
  const { loadedProfileData } = useProfile();
  const { isCallStatusEnabled, callState, hideBalances, setHideBalances, sessionId } = useApp();

  const p        = loadedProfileData?.profile;
  // Accounts may be at top-level or nested under profile depending on how the
  // data was loaded (full persona vs. API response shape).
  const accounts = p?.accounts || loadedProfileData?.accounts || {};
  const customer = p?.customer || loadedProfileData?.customer || {};

  const fmtCad = v => v != null
    ? `$${Math.abs(Number(v)).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : null;
  const fmtCadInt = v => v != null
    ? `$${Math.round(Math.abs(Number(v))).toLocaleString('en-CA')}`
    : null;

  const firstName = customer.first_name
    || (loadedProfileData?.name && loadedProfileData.name !== 'Default'
      ? loadedProfileData.name.split(' ')[0]
      : null);

  // Build a normalized card for every account key we recognise.
  const allCards = Object.entries(accounts)
    .filter(([k]) => ACCT_META[k])
    .map(([key, acct]) => {
      const meta  = ACCT_META[key];
      const bal   = resolveBalance(acct);
      const label = acct?.account_type || meta.label;
      return { key, label, group: meta.group, isCredit: meta.isCredit,
               route: meta.route || null, balance: bal,
               accountNumber: acct?.account_number ?? null };
    })
    .filter(a => a.balance != null);

  // Group cards preserving GROUP_ORDER.
  const grouped = GROUP_ORDER.map(g => ({
    group: g,
    title: GROUP_LABEL[g],
    cards: allCards.filter(a => a.group === g),
  })).filter(g => g.cards.length > 0);

  // Hero total: stated net worth wins; otherwise sum deposits + invest – credit.
  const netWorth = customer.net_worth != null ? Number(customer.net_worth)
    : customer.total_assets_cad != null
      ? Number(customer.total_assets_cad) - Number(customer.total_liabilities || 0)
      : null;

  const totalBalance = netWorth != null ? netWorth : allCards.reduce((sum, a) => {
    const v = a.balance || 0;
    return sum + (a.isCredit ? -v : v);
  }, 0);

  const fmtTotal = totalBalance < 0
    ? `–${fmtCadInt(Math.abs(totalBalance))}`
    : fmtCadInt(totalBalance);

  const maskNum = n => (typeof n === 'string' && n.length >= 4) ? `••••${n.slice(-4)}` : n || '';

  return (
    // Single scroll container — top bar, call banner, balance hero, quick
    // actions, accounts, and promo all scroll together.
    <div className="screen home-screen bh-home">
      <div className="scroll-content home-scroll">
        {/* Plain top bar: no green fill */}
        <div className="bh-home-topbar">
          <button
            className="bh-icon-btn bh-icon-btn--badged"
            aria-label="Notifications"
            onClick={() => onNavigate('notifications')}
            data-badge="3"
          >
            <MdNotificationsNone />
          </button>
          <button
            className="bh-icon-btn"
            aria-label={hideBalances ? 'Show balances' : 'Hide balances'}
            aria-pressed={hideBalances}
            onClick={() => setHideBalances(v => !v)}
            title={hideBalances ? 'Show balances' : 'Hide balances'}
          >
            {hideBalances ? <MdVisibilityOff /> : <MdVisibility />}
          </button>
        </div>

        {/* Call status banner stays directly below the bar so it's still prominent */}
        {isCallStatusEnabled && (
          <div className="bh-home-banner-wrapper">
            <CallStatusBanner status={callState} onHangUp={onSetCallState} />
          </div>
        )}

        {/* Green balance hero — the only place green is used as a fill */}
        <section className="bh-balance-hero" aria-labelledby="bh-balance-label">
          <div className="bh-balance-hero-greeting">
            <p className="bh-balance-hero-eyebrow">Welcome back,</p>
            <h1 className="bh-balance-hero-name">{firstName || 'there'}</h1>
          </div>

          <div className="bh-balance-card">
            <p id="bh-balance-label" className="bh-balance-card-label">Total Balance</p>
            <p className="bh-balance-card-amount">{maskAmount(fmtTotal, hideBalances)}</p>
          </div>
        </section>

        <div className="bh-home-main">
        {/* "Today" feed — Day in the Life situations IVA noticed (top of hub) */}
        <TodayCard sessionId={sessionId} />

        {/* Quick Actions — white cards, green icon dot, label below */}
        <section className="bh-section">
          <h2 className="bh-section-title">Quick Actions</h2>
          <div className="bh-qa-grid">
            <button className="bh-qa-card" onClick={() => onNavigate('emailTransfer')}>
              <div className="bh-qa-icon"><MdSend /></div>
              <span>Send Money</span>
            </button>
            <button className="bh-qa-card" onClick={() => onNavigate('accountTransfer')}>
              <div className="bh-qa-icon"><MdSwapHoriz /></div>
              <span>Transfer</span>
            </button>
            <button className="bh-qa-card" onClick={() => onNavigate('mobileDeposit')}>
              <div className="bh-qa-icon"><MdCameraAlt /></div>
              <span>Deposit</span>
            </button>
            <button className="bh-qa-card" onClick={() => onNavigate('billPayment')}>
              <div className="bh-qa-icon"><MdReceipt /></div>
              <span>Pay Bill</span>
            </button>
            <button className="bh-qa-card" onClick={() => onNavigate('search')}>
              <div className="bh-qa-icon"><MdSearch /></div>
              <span>Search</span>
            </button>
          </div>
        </section>

        {/* Accounts grouped by type */}
        {grouped.map(({ group, title, cards }) => (
          <section key={group} className="bh-section">
            <h2 className="bh-section-title">{title}</h2>
            <div className="bh-account-list">
              {cards.map(a => (
                <button
                  key={a.key}
                  className="bh-account-card"
                  onClick={() => a.route ? onNavigate(a.route) : onNavigate('accountDetails', a.key)}
                >
                  <p className="bh-account-card-type">{a.label}</p>
                  <p className={`bh-account-card-balance${a.isCredit && a.balance > 0 ? ' is-debt' : ''}`}>
                    {maskAmount(a.isCredit && a.balance > 0 ? `–${fmtCad(a.balance)}` : fmtCad(a.balance), hideBalances)}
                  </p>
                  {a.accountNumber && (
                    <p className="bh-account-card-number">{maskNum(a.accountNumber)}</p>
                  )}
                </button>
              ))}
            </div>
          </section>
        ))}

        {/* Promo banner kept at the bottom */}
        <div className="home-promo-banner">
          <img
            src="https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&auto=format&fit=crop&q=80"
            alt="Financial planning"
            className="home-promo-img"
          />
          <div className="home-promo-overlay">
            <div className="home-promo-top">
              <div className="home-promo-tag">Financial Advice</div>
              <div className="home-promo-headline">Make your money work harder for you</div>
            </div>
            <div className="home-promo-bottom">
              <button className="home-promo-btn">Explore</button>
            </div>
          </div>
        </div>
        </div>{/* /.bh-home-main */}
      </div>{/* /.scroll-content */}
    </div>
  );
};

export default HomeScreen;
